import { FormEvent, useEffect, useState } from 'react';
import AdminLayout from '../AdminLayout';
import { createWorksItem, deleteWorksItem, fetchAdminWorks, reorderWorks, updateWorksItem } from '../adminService';
import { useAdminSession } from '../adminSession';
import { ApiError, apiUrl } from '../../../services/apiClient';
import {
  AdminWorksItem,
  WORKS_CATEGORIES,
  WorksCategory,
  WorksItemInput,
  worksCategoryLabel,
} from '../../Works/worksService';
import formStyles from '../Portfolio/AdminPortfolioForm.module.scss';
import listStyles from '../Portfolio/AdminPortfolio.module.scss';
import styles from './AdminWorks.module.scss';

const EMPTY_FORM: WorksItemInput = {
  category: WORKS_CATEGORIES[0].id,
  title: '',
  clientName: '',
  description: '',
  isPublished: true,
};

// 사용자 웹 "포트폴리오"(/works)에 노출되는 G-WORKS 작업물 관리
function AdminWorks() {
  const [session, setSession] = useAdminSession();
  const [items, setItems] = useState<AdminWorksItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [filter, setFilter] = useState<WorksCategory | 'all'>('all');
  // null: 편집 안 함, 'new': 새 작업물, 숫자: 해당 id 수정
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<WorksItemInput>(EMPTY_FORM);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleError = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 401) {
      setSession(null);
    } else {
      setError(err instanceof Error ? err.message : fallback);
    }
  };

  const load = () => {
    if (!session) return;
    fetchAdminWorks(session.token)
      .then(setItems)
      .catch((err) => handleError(err, '포트폴리오 목록을 불러오지 못했습니다.'));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [session]);

  useEffect(
    () => () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    },
    [imagePreview],
  );

  const handleImageChange = (file: File | null) => {
    setImageFile(file);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  const startEdit = (item: AdminWorksItem | null) => {
    setFormError(null);
    handleImageChange(null);
    if (item) {
      setEditing(item.id);
      setExistingImageUrl(item.imageUrl);
      setForm({
        category: item.category,
        title: item.title,
        clientName: item.clientName ?? '',
        description: item.description,
        isPublished: item.isPublished,
      });
    } else {
      setEditing('new');
      setExistingImageUrl(null);
      setForm(filter === 'all' ? EMPTY_FORM : { ...EMPTY_FORM, category: filter });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setField = <K extends keyof WorksItemInput>(key: K, value: WorksItemInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!session || editing === null) return;
    if (!form.title.trim()) {
      setFormError('작업명을 입력해주세요.');
      return;
    }
    if (editing === 'new' && !imageFile) {
      setFormError('대표 이미지를 첨부해주세요.');
      return;
    }

    setFormError(null);
    setIsBusy(true);
    try {
      const saved =
        editing === 'new'
          ? await createWorksItem(session.token, form, imageFile as File)
          : await updateWorksItem(session.token, editing, form, imageFile);
      setItems((prev) =>
        editing === 'new' ? [saved, ...(prev ?? [])] : (prev ?? []).map((item) => (item.id === saved.id ? saved : item)),
      );
      setEditing(null);
      handleImageChange(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSession(null);
      } else {
        setFormError(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.');
      }
    } finally {
      setIsBusy(false);
    }
  };

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    setIsBusy(true);
    try {
      await action();
    } catch (err) {
      handleError(err, '처리 중 오류가 발생했습니다.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleMove = (index: number, offset: -1 | 1) => {
    if (!session || !items) return;
    const ids = items.map((item) => item.id);
    const target = index + offset;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    run(async () => setItems(await reorderWorks(session.token, ids)));
  };

  const handleDelete = (item: AdminWorksItem) => {
    if (!session) return;
    if (!window.confirm(`"${item.title}" 작업물을 삭제하시겠습니까? 되돌릴 수 없습니다.`)) return;
    run(async () => {
      await deleteWorksItem(session.token, item.id);
      setItems((prev) => (prev ?? []).filter((i) => i.id !== item.id));
      if (editing === item.id) setEditing(null);
    });
  };

  const visibleItems = items?.filter((item) => filter === 'all' || item.category === filter) ?? null;
  const previewUrl = imagePreview ?? (existingImageUrl ? apiUrl(existingImageUrl) : null);

  return (
    <AdminLayout title="포트폴리오 관리">
      {editing !== null && (
        <div className="section">
          <div className="sec-head">
            <div className="sec-title">{editing === 'new' ? '새 포트폴리오 등록' : '포트폴리오 수정'}</div>
            <div className="sec-actions">
              <button type="button" className="btn btn-outline" onClick={() => setEditing(null)}>
                취소
              </button>
            </div>
          </div>

          <form className={formStyles.form} onSubmit={handleSubmit}>
            <div className="card">
              <div className={formStyles.grid}>
                <div className={formStyles.field}>
                  <label>
                    분야<span className={formStyles.required}>*</span>
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setField('category', e.target.value as WorksCategory)}
                  >
                    {WORKS_CATEGORIES.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={formStyles.field}>
                  <label>
                    작업명<span className={formStyles.required}>*</span>
                  </label>
                  <input
                    value={form.title}
                    maxLength={200}
                    onChange={(e) => setField('title', e.target.value)}
                    placeholder="예: 카페 브랜드 아이덴티티"
                  />
                </div>
                <div className={formStyles.field}>
                  <label>고객사</label>
                  <input
                    value={form.clientName}
                    maxLength={100}
                    onChange={(e) => setField('clientName', e.target.value)}
                    placeholder="예: OO커피"
                  />
                </div>
                <div className={`${formStyles.field} ${formStyles.full}`}>
                  <label>설명</label>
                  <textarea
                    rows={4}
                    value={form.description}
                    maxLength={2000}
                    onChange={(e) => setField('description', e.target.value)}
                    placeholder="작업 범위, 컨셉 등을 적어주세요. 줄바꿈은 그대로 표시됩니다."
                  />
                </div>
                <div className={formStyles.field}>
                  <label className={formStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.isPublished}
                      onChange={(e) => setField('isPublished', e.target.checked)}
                    />
                    포트폴리오 페이지에 공개
                  </label>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-title" style={{ marginBottom: 12 }}>
                대표 이미지{editing === 'new' && <span className={formStyles.required}>*</span>}
              </div>
              {previewUrl && <img src={previewUrl} alt="미리보기" className={styles.imagePreview} />}
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={(e) => handleImageChange(e.target.files?.[0] ?? null)}
              />
              <p className={styles.hint}>
                jpg · png · gif · webp, 최대 20MB. {editing !== 'new' && '새 파일을 선택하면 기존 이미지가 교체됩니다.'}
              </p>
            </div>

            {formError && (
              <div className="card" style={{ color: '#B91C1C' }}>
                {formError}
              </div>
            )}

            <div className={formStyles.submitRow}>
              <button type="submit" className="btn btn-primary" disabled={isBusy}>
                {isBusy ? '저장 중...' : editing === 'new' ? '등록하기' : '저장하기'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="section">
        <div className="sec-head">
          <div className="sec-title">포트폴리오 항목</div>
          <div className="sec-actions">
            <button
              type="button"
              className={`chip ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              전체 {items?.length ?? 0}건
            </button>
            {WORKS_CATEGORIES.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`chip ${filter === category.id ? 'active' : ''}`}
                onClick={() => setFilter(category.id)}
              >
                {category.label}
              </button>
            ))}
            <button type="button" className="btn btn-primary" onClick={() => startEdit(null)}>
              + 새 포트폴리오 등록
            </button>
          </div>
        </div>

        {error && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>순서</th>
                  <th>이미지</th>
                  <th>작업명</th>
                  <th>분야</th>
                  <th>고객사</th>
                  <th>공개</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {!visibleItems && (
                  <tr>
                    <td colSpan={7} className="emptyRow">
                      불러오는 중...
                    </td>
                  </tr>
                )}
                {visibleItems && visibleItems.length === 0 && (
                  <tr>
                    <td colSpan={7} className="emptyRow">
                      등록된 포트폴리오가 없습니다.
                    </td>
                  </tr>
                )}
                {visibleItems?.map((item) => {
                  const index = items!.indexOf(item);
                  return (
                    <tr key={item.id} onClick={() => startEdit(item)}>
                      <td onClick={(event) => event.stopPropagation()}>
                        <div className={listStyles.rowActions}>
                          <button
                            type="button"
                            className="btn btn-outline"
                            aria-label="위로"
                            disabled={isBusy || filter !== 'all' || index === 0}
                            onClick={() => handleMove(index, -1)}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline"
                            aria-label="아래로"
                            disabled={isBusy || filter !== 'all' || index === items!.length - 1}
                            onClick={() => handleMove(index, 1)}
                          >
                            ↓
                          </button>
                        </div>
                      </td>
                      <td>
                        <img src={apiUrl(item.imageUrl)} alt={item.title} className={listStyles.thumb} />
                      </td>
                      <td className={styles.titleCell}>{item.title}</td>
                      <td>{worksCategoryLabel(item.category)}</td>
                      <td>{item.clientName ?? '-'}</td>
                      <td>
                        <span className={`badgePill ${item.isPublished ? 'ok' : 'neutral'}`}>
                          <span className="pulse"></span> {item.isPublished ? '공개' : '비공개'}
                        </span>
                      </td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <div className={listStyles.rowActions}>
                          <button type="button" className="btn btn-outline" onClick={() => startEdit(item)}>
                            수정
                          </button>
                          <button
                            type="button"
                            className={`btn btn-outline ${listStyles.dangerBtn}`}
                            disabled={isBusy}
                            onClick={() => handleDelete(item)}
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <p className={styles.hint} style={{ marginTop: 10 }}>
          순서는 사용자 포트폴리오 페이지에 보이는 순서입니다. 순서 변경은 "전체" 보기에서 할 수 있습니다.
        </p>
      </div>
    </AdminLayout>
  );
}

export default AdminWorks;
